import { ShieldCheck, Server, Lock, ArrowRight, EyeOff } from "lucide-react";
import MatrixRain from "@/components/MatrixRain";
import { useI18n } from "@/contexts/I18nContext";

const LEVELS_BASE = [
  {
    id: "agile",
    fr: { name: "Agile", tag: "Démarrage rapide", desc: "Infrastructure partagée, mise en route en quelques heures. Idéal pour tester et itérer.", bullets: ["Déploiement immédiat","Supervision continue","Données chiffrées en transit"] },
    en: { name: "Agile", tag: "Fast start", desc: "Shared infrastructure, live in hours. Perfect to test and iterate.", bullets: ["Instant deployment","Continuous monitoring","Encrypted in transit"] },
    icon: "shield",
    accent: "#00cfff",
  },
  {
    id: "structure",
    fr: { name: "Structuré", tag: "Haute disponibilité", desc: "Environnement dédié, redondance, pilotage avancé. Adapté aux activités établies.", bullets: ["Instance dédiée","Redondance active","Sauvegarde quotidienne"] },
    en: { name: "Structured", tag: "High availability", desc: "Dedicated environment, redundancy, advanced controls. Built for established businesses.", bullets: ["Dedicated instance","Active redundancy","Daily backup"] },
    icon: "server",
    accent: "#deff9a",
  },
  {
    id: "souverain",
    fr: { name: "Souverain", tag: "Contrôle total", desc: "100% installé chez vous ou sur votre cloud. Vous restez propriétaire — à vie.", bullets: ["Hébergement chez vous","Données 100% internes","Audit & traçabilité"] },
    en: { name: "Sovereign", tag: "Full control", desc: "100% deployed on-premise or your private cloud. You stay the owner — forever.", bullets: ["On-premise hosting","100% internal data","Audit & traceability"] },
    icon: "lock",
    accent: "#e0e7ff",
  },
];

function Glyph({ kind, color }) {
  const cls = "w-5 h-5";
  if (kind === "shield") return <ShieldCheck className={cls} style={{ color }} />;
  if (kind === "server") return <Server className={cls} style={{ color }} />;
  return <Lock className={cls} style={{ color }} />;
}

export default function Vault() {
  const { t, lang } = useI18n();

  return (
    <section data-testid="vault-page" className="dncm-stage min-h-[100vh] pt-28 pb-24">
      <div className="absolute inset-0 opacity-25 pointer-events-none">
        <MatrixRain density={0.55} speed={0.7} opacity={0.55} />
      </div>
      <div className="absolute inset-0 dncm-grid-bg pointer-events-none" />

      <div className="dncm-content max-w-7xl mx-auto px-6 lg:px-10">
        <header className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="dncm-pulse-dot" />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              The Vault
            </span>
          </div>
          <h2 data-testid="vault-title" className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            {t("vault_title").split("—")[0].trim()}
            <br />
            <span className="dncm-gradient-text">{t("vault_title").split("—")[1] ? t("vault_title").split("—")[1].trim() : ""}</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl text-base sm:text-lg">{t("vault_subtitle")}</p>
        </header>

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {LEVELS_BASE.map((l, idx) => {
            const text = lang === "en" ? l.en : l.fr;
            return (
              <article
                key={l.id}
                data-testid={`sov-level-${l.id}`}
                className="dncm-sov-card rounded-2xl p-6 dncm-fadein"
                style={{ animationDelay: `${0.06 * idx}s` }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="flex items-center justify-center w-10 h-10 rounded-lg"
                    style={{
                      background: `${l.accent}1A`,
                      border: `1px solid ${l.accent}55`,
                      boxShadow: `0 0 18px ${l.accent}33`,
                    }}
                  >
                    <Glyph kind={l.icon} color={l.accent} />
                  </span>
                  <div>
                    <div className="font-display text-xl text-white leading-none">
                      {lang === "en" ? `Level ${idx + 1}` : `Niveau ${idx + 1}`} · {text.name}
                    </div>
                    <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55 mt-1">
                      {text.tag}
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-white/70 text-sm leading-relaxed">{text.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {text.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-white/70">
                      <ArrowRight className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: l.accent }} />
                      {b}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* GAFAM independence */}
        <div data-testid="gafam-section" className="mt-16 dncm-card rounded-2xl p-6 sm:p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <EyeOff className="w-4 h-4 text-[color:var(--dncm-green)]" />
              <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
                {t("gafam_title")}
              </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-white">
              {lang === "fr"
                ? "Vos données ne nourrissent pas les géants américains."
                : "Your data does not feed the US giants."}
            </h3>
            <p className="mt-3 text-white/65 max-w-2xl">{t("gafam_text")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["GAFAM", "USA Cloud", "Tracking"].map((tag) => (
              <span key={tag} className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45 line-through">
                {tag}
              </span>
            ))}
            <span className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-[color:var(--dncm-green)]">
              ✓ {lang === "fr" ? "Indépendant" : "Independent"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
